import React, {
    useCallback,
    useRef,
    useState,
    useEffect,
    useMemo,
} from 'react';
import { graphql } from 'gatsby';

import { className, isMobile } from 'design-system';
import ThemeHeader from 'design-system/src/components/Gatsby/Header';
import HeaderDropdownComponent from '../HeaderDropdownComponent';
import TransitionLink from '../TransitionLink';
import ThemePicker, {
    changeDocumentColors,
    LOCAL_STORAGE_COLOR,
} from '../ThemePicker';
import SvgTint from '../SvgTint';
import Logo from '../Logo';
import SearchIcon from '../SearchIcon';

import 'design-system/src/scss/fonts.scss';
import 'design-system/src/scss/global.scss';

import style from './header.module.scss';
import {
    getLanguageNavByAlternateLanguages,
    getLanguageHref,
} from '../../utils';

function orderByPosition(array) {
    return array.sort((a, b) => {
        if (a.position < b.position) {
            return -1;
        }
        if (a.position > b.position) {
            return 1;
        }
        return 0;
    });
}

const groupNav = (navs, onClick, dark, align, language) =>
    navs.map((item) => {
        const hasChilds = !!item.childLinks.length;
        const isGatsby =
            !hasChilds && item.href && item.href.substring(0, 4) !== 'http';

        const isSearch = !!item.childLinks.find(
            (subitem) => subitem.label === 'searchform',
        );

        return {
            ...item,
            Tag: hasChilds ? 'button' : isGatsby ? TransitionLink : 'a',
            scheme: isGatsby ? 'gatsby' : null,
            partiallyActive: true,
            activeClassName: style['item__link--active'],
            title: isSearch ? <SearchIcon dark={dark} /> : item.label,
            titleAlt: item.label,
            type: hasChilds ? 'list' : '',
            extraClassName: `${style.item} ${isSearch ? 'd-none d-lg-inline-block' : ''
                }`,
            linkClassName: `${style['item__link']} ${hasChilds ? style['item__link--dropdown'] : ''
                } ${isSearch ? style['item__link--search'] : ''} ${dark ? style['item__link--dark'] : ''
                }`,
            dropdownOn: 'click',
            dropdown: hasChilds ? (
                <HeaderDropdownComponent
                    items={orderByPosition(item.childLinks)}
                    onClick={onClick}
                    align={align}
                    language={language}
                />
            ) : null,
        };
    });

export default function Header({
    alternateLanguages,
    data,
    layoutProps,
    sliceContext,
}) {
    const themePickerRef = useRef();
    const navbarRef = useRef();
    const [desktopDropdownShown, setDesktopDropdownShown] = useState(false);
    const [themePickerStatus, setThemePickerStatus] = useState(null);
    const [isInScroll, setIsInScroll] = useState(false);
    const [isMobileShown, setIsMobileShown] = useState(false);

    const languageNav = useMemo(
        () =>
            getLanguageNavByAlternateLanguages(
                alternateLanguages,
                layoutProps?.specificLanguages,
            ),
        [alternateLanguages, layoutProps?.specificLanguages],
    );

    useEffect(() => {
        if (typeof window === 'undefined' || !data.naeColors?.edges) {
            return;
        }

        if (layoutProps?.colorScheme === 'Default') {
            changeDocumentColors(null);
        } else if (layoutProps?.colorScheme) {
            const colorScheme = data.naeColors.edges.find(
                ({ node }) => node.colorId === layoutProps.colorScheme,
            );
            if (colorScheme?.node) {
                changeDocumentColors(colorScheme.node);
            }
        } else {
            let defaultColor = window.localStorage.getItem(LOCAL_STORAGE_COLOR);
            if (defaultColor) {
                defaultColor = JSON.parse(defaultColor);
                changeDocumentColors(defaultColor);
            }
        }
    }, [layoutProps?.colorScheme, data.naeColors]);

    const closeThemePicker = useCallback(() => {
        if (themePickerStatus) {
            themePickerRef.current.close();
        }
    }, [themePickerRef, themePickerStatus]);

    const closeNavbar = useCallback(() => {
        if (navbarRef?.current) {
            navbarRef.current?.toggleCollapse(false)();
            navbarRef.current?.closeDropdown();
        }
    }, []);

    const closeDropdown = useCallback(() => {
        if (desktopDropdownShown && navbarRef?.current?.closeDropdown) {
            navbarRef.current.closeDropdown();
        }
    }, [desktopDropdownShown]);

    const getDropdownCollapseClassName = useCallback(
        (show, isFullyClosed, isFullyOpened) =>
            `${style.dropdown} ${show ? style['dropdown--open'] : ''} ${isFullyOpened ? style['dropdown--open-finish'] : ''
            }`,
        [],
    );

    const onDropdownOpen = useCallback(() => {
        setDesktopDropdownShown(isMobile() ? false : true);
        closeThemePicker();
    }, [setDesktopDropdownShown, closeThemePicker]);

    const onDropdownClose = useCallback(() => {
        setDesktopDropdownShown(false);
    }, [setDesktopDropdownShown]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const onResizeListener = () => {
            if (isMobile()) {
                setDesktopDropdownShown(false);
            }
        };

        window.addEventListener('resize', onResizeListener);
        return () => {
            window.removeEventListener('resize', onResizeListener);
        };
    }, [setDesktopDropdownShown]);

    const wrapperClassName = useCallback(
        (isScrolled, isMobileShown) => {
            setTimeout(() => {
                setIsInScroll(isScrolled);
                setIsMobileShown(isMobileShown);
            }, 0);

            return isScrolled ? style['navbar--in-scroll'] : '';
        },
        [setIsInScroll, setIsMobileShown],
    );

    const dark =
        desktopDropdownShown || themePickerStatus === 'open'
            ? false
            : layoutProps?.dark
                ? true
                : isInScroll || isMobileShown
                    ? true
                    : false;

    const onThemePickerClick = useCallback(() => {
        if (themePickerStatus) {
            themePickerRef.current.close();
        } else {
            closeDropdown();
            themePickerRef.current.open();
        }
    }, [themePickerRef, themePickerStatus, closeDropdown]);

    return (
        <>
            <ThemeHeader
                title={data.site.siteMetadata.title}
                navbarRef={navbarRef}
                className={`${style.navbar} ${desktopDropdownShown ? style['navbar--dropdown-open'] : ''
                    } ${style[`navbar--${layoutProps.dark ? 'dark' : 'light'}`]} ${layoutProps?.minimal ? style.minimal : ''
                    } ${themePickerStatus === 'open'
                        ? style['navbar--theme-picker-open']
                        : ''
                    }`}
                wrapperClassName={wrapperClassName}
                isFixed={true}
                isDark={
                    desktopDropdownShown || themePickerStatus === 'open'
                        ? false
                        : layoutProps?.dark
                }
                isTransparent={
                    desktopDropdownShown || themePickerStatus === 'open'
                        ? false
                        : layoutProps?.dark
                }
                blockScrollStyle={
                    desktopDropdownShown || themePickerStatus === 'open'
                        ? true
                        : false
                }
                customTogglerIcon={style['navbar__toggler']}
                customTogglerIconOpen={style['navbar__toggler--open']}
                onDropdownOpen={onDropdownOpen}
                onDropdownClose={onDropdownClose}
                dropdownCollapseClassName={getDropdownCollapseClassName}
                navbarBrandHref={getLanguageHref(sliceContext.language)}
                dropdownClassName={
                    className(`${style['dropdown__inner']}`).className
                }
                dropdownOverlay={true}
                logoDom={<Logo dark={dark} />}
                customToggler={style.toggler}
                navGroups={[
                    !layoutProps?.minimal && {
                        align: 'left',
                        items: groupNav(
                            data.links.nodes,
                            closeNavbar,
                            dark,
                            'left',
                            sliceContext.language,
                        ),
                        className: style['main-navbar'],
                    },
                    {
                        align: 'right',
                        items: languageNav
                            ? groupNav(
                                languageNav,
                                closeNavbar,
                                dark,
                                'right',
                                layoutProps.language,
                            )
                            : [],
                        className: style['lang-navbar'],
                    },
                    !layoutProps?.minimal && {
                        align: 'right',
                        items: [
                            {
                                extraClassName: ``,
                                linkClassName: `${style['color-icon']} ${style[
                                    `color-icon--${themePickerStatus
                                        ? themePickerStatus
                                        : desktopDropdownShown
                                            ? 'open'
                                            : ''
                                    }`
                                ]
                                    }`,
                                scheme: 'button',
                                size: 'sm',
                                Tag: 'button',
                                title: <SvgTint dark={dark} />,
                                titleAlt: 'Colors',
                                onClick: onThemePickerClick,
                            },
                        ],
                        className: style['color-navbar'],
                    },
                ].filter((i) => !!i)}
            />
            <ThemePicker
                ref={themePickerRef}
                language={sliceContext.language}
                rawColors={data.naeColors}
                rawConcepts={data.naeConcepts}
                onStatusChange={setThemePickerStatus}
            />
        </>
    );
}

export const query = graphql`
    fragment HeaderLink on DatoCmsHeader {
        label
        href
        openInNewWindow
    }
    query ($language: String) {
        site {
            siteMetadata {
                title
            }
        }
        # logo: file(
        #     sourceInstanceName: { eq: "img-nae" }
        #     name: { eq: "logo" }
        # ) {
        #     publicURL
        # }
        # logoWhite: file(
        #     sourceInstanceName: { eq: "img-nae" }
        #     name: { eq: "logo-white" }
        # ) {
        #     publicURL
        # }
        links: allDatoCmsHeader(
            filter: {
                treeParent: { id: { eq: null } }
                locale: { eq: $language }
            }
            sort: { position: ASC }
        ) {
            nodes {
                ...HeaderLink
                childLinks: treeChildren {
                    column
                    position
                    ...HeaderLink
                }
            }
        }
        naeColors: allNaeColor {
            edges {
                node {
                    colorId
                    primary
                    secondary
                    tertiary
                    fourth
                }
            }
        }
        naeConcepts: allNaeConcept {
            edges {
                node {
                    en
                    es
                    cat
                    conceptId
                    colors {
                        r
                        color {
                            colorId
                        }
                    }
                }
            }
        }
    }
`;
